import { LoruleRequestConfig } from '@galaxy/lorule-react-commons';
import { callWebUtils, commonHeaders } from './commonApiUtil';
import {
    ActionMemo,
    ActionMemoInfo,
    CreateActionMemoType,
    AddSupplementActionMemoType,
    CopyActionMemoType,
    UpdateActionMemoType,
    ApproverItem,
    DistributionItem,
} from './actionMemoApiTypes';

export const getActionMemo = async (
    config: LoruleRequestConfig,
    id: number,
    user: boolean = true,
) => callWebUtils<ActionMemo>(config, `/actionmemo/${id}?user=${user}`, 'GET', null, commonHeaders);

export const copyActionMemo = async (
    config: LoruleRequestConfig,
    actionMemo: CopyActionMemoType,
    user: boolean = true,
) =>
    callWebUtils<ActionMemo>(
        config,
        `/actionmemo/?user=${user}`,
        'POST',
        JSON.stringify({
            ...actionMemo,
            initType: 'COPY',
        }),
        commonHeaders,
    );

export const addSupplement = async (
    config: LoruleRequestConfig,
    actionMemo: AddSupplementActionMemoType,
    user: boolean = true,
) =>
    callWebUtils<ActionMemo>(
        config,
        `/actionmemo/?user=${user}`,
        'POST',
        JSON.stringify({
            ...actionMemo,
            initType: 'SUPPLEMENT',
        }),
        commonHeaders,
    );

export const createActionMemo = async (
    config: LoruleRequestConfig,
    actionMemo: CreateActionMemoType,
    user: boolean = true,
) =>
    callWebUtils<ActionMemoInfo>(
        config,
        `/actionmemo?user=${user}`,
        'POST',
        JSON.stringify(actionMemo),
        commonHeaders,
    );

export const updateActionMemo = async (
    config: LoruleRequestConfig,
    amId: number,
    actionMemo: UpdateActionMemoType,
    user: boolean = true,
) =>
    callWebUtils<ActionMemoInfo>(
        config,
        `/actionmemo/${amId}?user=${user}`,
        'PUT',
        JSON.stringify(actionMemo),
        commonHeaders,
    );

export const changeCompletionDate = async (
    config: LoruleRequestConfig,
    amId: number,
    completionDate: Date,
    commentMessage?: string | null,
    user: boolean = true,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/completionDate?user=${user}`,
        'PUT',
        JSON.stringify({
            completionDate,
            commentMessage,
        }),
        commonHeaders,
    );

export const markConfidential = async (
    config: LoruleRequestConfig,
    amId: number,
    confidential: boolean,
    commentMessage?: string | null,
    user: boolean = true,
) =>
    callWebUtils<ActionMemoInfo>(
        config,
        `/actionmemo/${amId}/confidential?user=${user}`,
        'PUT',
        JSON.stringify({
            confidential,
            commentMessage,
        }),
        commonHeaders,
    );

export const updateContractService = async (
    config: LoruleRequestConfig,
    amId: number,
    nclDepartmentCode: string,
    nclDevCode: string,
) =>
    callWebUtils<ActionMemoInfo>(
        config,
        `/actionmemo/${amId}/contractService`,
        'PUT',
        JSON.stringify({
            nclDepartmentCode,
            nclDevCode,
        }),
        commonHeaders,
    );

export const updateApprovers = async (
    config: LoruleRequestConfig,
    amId: number,
    approvers: ApproverItem[],
    user: boolean = true,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/approver?user=${user}`,
        'PUT',
        JSON.stringify(
            approvers.map((approver) => ({
                ...approver,
                amId,
            })),
        ),
        commonHeaders,
    );

export const skipApprover = async (
    config: LoruleRequestConfig,
    amId: number,
    approverId: number,
    commentMessage?: string | null,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/approver/${approverId}`,
        'PATCH',
        JSON.stringify({
            amId,
            approverId,
            commentMessage,
            skip: true,
        }),
        commonHeaders,
    );

export const unskipApprover = async (
    config: LoruleRequestConfig,
    amId: number,
    approverId: number,
    commentMessage?: string | null,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/approver/${approverId}`,
        'PATCH',
        JSON.stringify({
            amId,
            approverId,
            commentMessage,
            skip: false,
        }),
        commonHeaders,
    );

// Same as viewers
export const updateDistributions = async (
    config: LoruleRequestConfig,
    amId: number,
    distributions: DistributionItem[],
    user: boolean = true,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/distribution?user=${user}`,
        'PUT',
        JSON.stringify(
            distributions.map((distribution) => ({
                ...distribution,
                amId,
            })),
        ),
        commonHeaders,
    );

export const addComment = async (
    config: LoruleRequestConfig,
    amId: number,
    commentMessage: string,
    user: boolean = true,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/comment?user=${user}`,
        'POST',
        JSON.stringify({
            commentMessage,
        }),
        commonHeaders,
    );

export const addWatcher = async (config: LoruleRequestConfig, amId: number) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/watcher`,
        'POST',
        JSON.stringify({
            amId,
        }),
        commonHeaders,
    );

export const removeWatcher = async (config: LoruleRequestConfig, amId: number) =>
    callWebUtils(config, `/actionmemo/${amId}/watcher`, 'DELETE', null, commonHeaders);

export const replaceProjectManager = async (
    config: LoruleRequestConfig,
    amId: number,
    personId: number,
    commentMessage?: string | null,
    user: boolean = true,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/projectManager?user=${user}`,
        'PUT',
        JSON.stringify({
            personId,
            commentMessage,
        }),
        commonHeaders,
    );

export const replaceContractSigner = async (
    config: LoruleRequestConfig,
    amId: number,
    contractOrganizationId?: number | null,
    contractSigner?: number | null,
    commentMessage?: string | null,
) =>
    callWebUtils(
        config,
        `/actionmemo/${amId}/contractSigner`,
        'PUT',
        JSON.stringify({
            amId,
            contractOrganizationId,
            contractSigner,
            commentMessage,
        }),
        commonHeaders,
    );
